// CD: backend
import { defineOnce, emit, EVENTS, fetchSectionDocument, routesRoot, showToast } from './pushe-ui.js';

const queues = new Map();
const validCart = (cart) => cart && Number.isInteger(cart.item_count) && cart.item_count >= 0
  && Array.isArray(cart.items) && cart.items.every((item) => typeof item.key === 'string'
    && Number.isInteger(item.quantity) && item.quantity > 0);

const discountCodes = (value) => {
  const codes = new Map();
  for (const code of value.split(',').map((code) => code.trim()).filter(Boolean)) {
    if (!codes.has(code.toLowerCase())) codes.set(code.toLowerCase(), code);
  }
  return [...codes.values()];
};
const productIds = (cart) => {
  const ids = cart?.items?.map((item) => item.product_id ?? item.product?.id);
  return ids?.every((id) => id != null) ? JSON.stringify([...new Set(ids.map(String))].sort()) : null;
};

function visibleModal(modal) {
  if (modal.closest('[hidden],dialog:not([open])')) return false;
  if (typeof modal.checkVisibility === 'function') {
    return modal.checkVisibility({ checkVisibilityCSS: true, checkOpacity: true, contentVisibilityAuto: true });
  }
  if (typeof modal.getClientRects === 'function' && !modal.getClientRects().length) return false;
  for (let element = modal; element; element = element.parentElement) {
    const style = window.getComputedStyle?.(element);
    if (style && (style.display === 'none' || ['hidden', 'collapse'].includes(style.visibility)
      || style.contentVisibility === 'hidden' || style.opacity === '0')) return false;
  }
  return true;
}

// Include body parsing in the timeout; a response can lose its body after headers arrive.
async function requestJson(url, options = {}, signal) {
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) controller.abort();
  signal?.addEventListener('abort', abort, { once: true });
  const timer = setTimeout(abort, 10000);
  try {
    controller.signal.throwIfAborted();
    const response = await fetch(url, { ...options, signal: controller.signal });
    const data = await response.json();
    controller.signal.throwIfAborted();
    return { response, data };
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener('abort', abort);
  }
}

async function readCart(root, signal) {
  const { response, data } = await requestJson(`${root}cart.js`, { cache: 'no-store' }, signal);
  if (!response.ok || !validCart(data)) throw new Error('cart_read_failed');
  return data;
}

/** Serializes cart mutations (including different keys) until reconciliation finishes. Never retries a POST. */
export function applyLineChange({ root = routesRoot(), key, quantity, sections = ['pushe-main-cart', 'pushe-header'], signal }) {
  if (typeof key !== 'string' || !key || !Number.isInteger(quantity) || quantity < 0) {
    return Promise.reject(new TypeError('invalid_cart_change'));
  }
  return mutateCart({ root, endpoint: 'change', payload: { id: key, quantity }, sections, signal });
}

export function applyDiscountChange({ root = routesRoot(), discount, sections = ['pushe-main-cart'], signal }) {
  if (typeof discount !== 'string') return Promise.reject(new TypeError('invalid_cart_discount'));
  return mutateCart({ root, endpoint: 'update', payload: { discount: discountCodes(discount).join(',') }, sections, signal });
}

/** The same queue covers quantities and codes. A lost POST is reconciled, never retried. */
function mutateCart({ root, endpoint, payload, sections, signal }) {
  const previous = queues.get(root) ?? Promise.resolve();
  const pending = previous.catch(() => ({ ok: false, kind: 'network', cart: null })).then(async (prior) => {
    if (signal?.aborted) return { ok: false, kind: 'aborted', cart: null };
    if (prior?.ok === false && !prior.cart) return { ok: false, kind: 'network', cart: null };
    let kind = 'network';
    let message;
    try {
      const { response, data } = await requestJson(`${root}cart/${endpoint}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ ...payload, sections, sections_url: `${root}cart` }),
      }, signal);
      if (response.ok && validCart(data)) {
        emit(EVENTS.CART_UPDATED, { item_count: data.item_count });
        return { ok: true, cart: data, sections: data.sections ?? {} };
      }
      kind = response.status === 422 ? 'stock' : 'network';
      message = typeof data?.description === 'string' ? data.description
        : typeof data?.message === 'string' ? data.message : undefined;
    } catch {
      // A lost response does not mean Shopify rejected the change.
    }
    if (signal?.aborted) return { ok: false, kind: 'aborted', cart: null };
    let cart = null;
    try { cart = await readCart(root, signal); } catch { /* Fail closed when truth is unavailable. */ }
    if (cart) emit(EVENTS.CART_UPDATED, { item_count: cart.item_count });
    return { ok: false, kind, message, cart };
  });
  queues.set(root, pending);
  const clean = () => { if (queues.get(root) === pending) queues.delete(root); };
  pending.then(clean, clean);
  return pending;
}

/** Only Shopify's applicable codes or discount-code applications prove a discount was applied. */
export function cartDiscountCodes(cart) {
  const codes = new Map();
  for (const entry of cart?.discount_codes ?? []) {
    if (typeof entry.code === 'string') codes.set(entry.code.toLowerCase(), { code: entry.code, applicable: entry.applicable === true });
  }
  const applications = [...(cart?.discount_applications ?? []), ...(cart?.cart_level_discount_applications ?? []),
    ...(cart?.items ?? []).flatMap((item) => (item.line_level_discount_allocations ?? []).map((allocation) => allocation.discount_application))];
  for (const application of applications) {
    if (application?.type === 'discount_code' && typeof application.title === 'string') {
      codes.set(application.title.toLowerCase(), { code: application.title, applicable: true });
    }
  }
  return [...codes.values()];
}

/** +/- starts from the real cart; typed quantities are explicit requests, never input arithmetic. */
export function targetQuantity(cart, key, action, max = 99) {
  const item = cart?.items?.find((line) => line.key === key);
  if (!item) return null;
  if (action.remove) return 0;
  const ceiling = Math.max(item.quantity, Number(max) || 99);
  const quantity = action.delta ? item.quantity + action.delta : Number(action.value);
  if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > ceiling) return null;
  return quantity;
}

/** Keeps the custom-element root alive while replacing only Shopify-rendered children. */
export class PusheCart extends HTMLElement {
  #controller;
  #tail = Promise.resolve();
  #pending = 0;
  #failed = false;
  #unconfirmed = false;
  #extrasCleanup;
  #recommendationsController;
  #recommendationsProductIds;

  connectedCallback() {
    if (this.#controller && !this.#controller.signal.aborted) return;
    this.#unconfirmed = this.#unconfirmed || this.#pending > 0 || this.#failed || this.getAttribute('aria-busy') === 'true';
    this.#controller = new AbortController();
    this.#tail = Promise.resolve();
    this.#pending = 0;
    this.addEventListener('click', this.#click);
    this.addEventListener('change', this.#change);
    this.addEventListener('submit', this.#submit);
    this.#extras();
    if (this.#unconfirmed) {
      this.#pending = 1;
      this.setAttribute('aria-busy', 'true');
      this.#checkout();
      this.#tail = this.#resync(this.#controller.signal);
      return;
    }
    this.#checkout();
  }

  disconnectedCallback() {
    if (this.#pending || this.#failed) this.#unconfirmed = true;
    this.removeEventListener('click', this.#click);
    this.removeEventListener('change', this.#change);
    this.removeEventListener('submit', this.#submit);
    this.#extrasCleanup?.();
    this.#recommendationsController?.abort();
    this.#controller?.abort();
  }

  async #resync(signal) {
    try {
      const cart = await readCart(routesRoot(), signal);
      if (signal.aborted) return;
      await this.#render(cart, {}, signal);
      if (signal.aborted) return;
      this.#failed = false;
      this.#unconfirmed = false;
      emit(EVENTS.CART_UPDATED, { item_count: cart.item_count });
    } catch {
      if (signal.aborted) return;
      this.#failed = true;
      for (const control of this.querySelectorAll('button,input')) control.setAttribute('disabled', '');
      this.#error();
    } finally {
      if (this.#controller.signal === signal && !signal.aborted) {
        this.#pending = 0;
        this.setAttribute('aria-busy', 'false');
        this.#checkout();
      }
    }
  }

  #click = (event) => {
    if (event.target.closest?.('.pushe-summary > a,[data-cart-sticky] a') && (this.#pending || this.#failed || this.#unconfirmed)) {
      event.preventDefault();
      return;
    }
    const removeCode = event.target.closest?.('[data-discount-remove]');
    if (removeCode && this.contains(removeCode)) {
      event.preventDefault();
      if (!this.#pending) this.#enqueue(null, { discount: removeCode.dataset.discountRemove, remove: true });
      return;
    }
    const closeNotice = event.target.closest?.('[data-discount-notice] .pushe-toast__close');
    if (closeNotice) {
      const panel = closeNotice.closest('[data-discount-message]');
      panel.remove();
      const input = this.querySelector('[data-cart-discount-form] input');
      const remaining = input?.getAttribute('aria-describedby')?.split(' ').filter((id) => id !== panel.id).join(' ');
      if (remaining) input.setAttribute('aria-describedby', remaining);
      else input?.removeAttribute('aria-describedby');
      input?.focus({ preventScroll: true });
      return;
    }
    const button = event.target.closest?.('.pushe-qty__btn,.pushe-line__remove');
    if (!button || button.disabled || button.hasAttribute('disabled')) return;
    const line = button.closest('[data-line-key]');
    if (!line || !this.contains(line)) return;
    event.preventDefault();
    const action = button.classList.contains('pushe-line__remove') ? { remove: true }
      : { delta: button === line.querySelector('.pushe-qty__btn') ? -1 : 1 };
    this.#enqueue(line.dataset.lineKey, action);
  };

  #submit = (event) => {
    const form = event.target.closest?.('[data-cart-discount-form]');
    if (!form || !this.contains(form)) return;
    event.preventDefault();
    const code = form.querySelector('input')?.value.trim();
    if (!this.#pending && code) this.#enqueue(null, { discount: code });
  };

  #change = (event) => {
    const input = event.target.closest?.('.pushe-qty__value');
    const line = input?.closest('[data-line-key]');
    if (line && this.contains(line)) this.#enqueue(line.dataset.lineKey, { value: input.value });
  };

  #line(key) {
    // Keys can contain selector punctuation; never interpolate one into a selector.
    return Array.from(this.querySelectorAll('[data-line-key]')).find((line) => line.dataset.lineKey === key);
  }

  #checkout() {
    const busy = this.#pending || this.#failed || this.#unconfirmed;
    for (const control of this.querySelectorAll('[data-cart-discount-tools] input,[data-cart-discount-tools] button')) {
      control.toggleAttribute('disabled', Boolean(busy));
    }
    for (const link of this.querySelectorAll('.pushe-summary > a,[data-cart-sticky] a')) {
      if (busy) {
        if (link.hasAttribute('href')) link.dataset.cartHref = link.getAttribute('href');
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      } else if (link.dataset.cartHref) {
        link.setAttribute('href', link.dataset.cartHref);
        delete link.dataset.cartHref;
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      }
    }
  }

  #discountNotice({ error, message, code, removed = false, results }) {
    const notices = results ?? [{ error, message, code, removed }];
    const failures = notices.filter((notice) => notice.error);
    const noticeText = (notice) => {
      const text = notice.message || (notice.error ? this.dataset.codeError : notice.removed ? this.dataset.codeRemoved : this.dataset.codeApplied);
      return notices.length > 1 ? `${notice.code}: ${text}` : text;
    };
    const target = this.querySelector('[data-discount-notice]');
    if (!target) {
      if (this.ownerDocument.getElementById('pushe-toast-template')) {
        showToast({ message: notices.map(noticeText).join('\n'), tone: failures.length ? 'error' : 'success' });
      }
      return;
    }
    const input = this.querySelector('[data-cart-discount-form] input');
    if (input) {
      input.value = failures.map((notice) => notice.code).join(',');
      input.toggleAttribute('aria-invalid', failures.length > 0);
      if (failures.length) {
        input.setAttribute('aria-invalid', 'true');
        input.closest('.pushe-field').classList.add('pushe-field--error');
      } else {
        input.removeAttribute('aria-describedby');
        input.closest('.pushe-field').classList.remove('pushe-field--error');
      }
    }
    const panels = notices.map((notice, index) => {
      const template = this.querySelector(notice.error ? '[data-discount-error]' : '[data-discount-success]');
      const panel = template?.content.firstElementChild?.cloneNode(true);
      const body = panel?.querySelector('.pushe-toast__body');
      if (!body) return null;
      body.textContent = noticeText(notice);
      if (input) panel.id = `${input.id}-notice${index ? `-${index}` : ''}`;
      panel.setAttribute('data-discount-message', '');
      return panel;
    }).filter(Boolean);
    if (input && failures.length) input.setAttribute('aria-describedby', panels.filter((panel) => panel.getAttribute('role') === 'alert').map((panel) => panel.id).join(' '));
    target.replaceChildren(...panels);
  }

  #error(message) {
    showToast({ message: message || this.dataset.error, tone: 'error' });
  }

  #enqueue(key, action) {
    if (this.#failed || this.#unconfirmed || !this.#controller || this.#controller.signal.aborted) return;
    const signal = this.#controller.signal;
    this.#pending++;
    this.setAttribute('aria-busy', 'true');
    this.#checkout();
    this.#tail = this.#tail.then(async () => {
      let reported = false;
      try {
        if (signal.aborted || this.#failed) return;
        const root = routesRoot();
        const cart = await readCart(root, signal);
        if (signal.aborted) return;
        if (action.discount !== undefined) {
          const requested = discountCodes(action.discount);
          if (!requested.length) return;
          const requestedSet = new Set(requested.map((code) => code.toLowerCase()));
          const codes = cartDiscountCodes(cart).filter((entry) => !requestedSet.has(entry.code.toLowerCase())).map((entry) => entry.code);
          if (!action.remove) codes.push(...requested);
          const result = await applyDiscountChange({ root, discount: codes.join(','), sections: [this.dataset.sectionId], signal });
          if (signal.aborted) return;
          if (!result.cart) {
            this.#discountNotice({ error: true, message: result.message || this.dataset.error, code: action.discount });
            reported = true;
            throw new Error('cart_state_unknown');
          }
          const knownCodes = [result.cart.discount_codes, result.cart.discount_applications, result.cart.cart_level_discount_applications].some(Array.isArray);
          const returnedCodes = cartDiscountCodes(result.cart);
          await this.#render(result.cart, result.sections ?? {}, signal);
          if (signal.aborted) return;
          this.#discountNotice({ results: requested.map((code) => {
            const entry = returnedCodes.find((entry) => entry.code.toLowerCase() === code.toLowerCase());
            const error = action.remove ? !knownCodes || Boolean(entry) : !entry?.applicable;
            return { error, code, removed: action.remove,
              message: (!result.ok || !knownCodes) && error ? result.message || this.dataset.error : undefined };
          }) });
          return;
        }
        const max = this.#line(key)?.querySelector('input')?.getAttribute('max');
        const quantity = targetQuantity(cart, key, action, max);
        const current = cart.items.find((item) => item.key === key);
        if (quantity === null || quantity === current?.quantity) {
          if (quantity === null) this.#error();
          await this.#render(cart, {}, signal);
          return;
        }
        const result = await applyLineChange({ root, key, quantity, sections: [this.dataset.sectionId], signal });
        if (signal.aborted) return;
        if (!result.ok) {
          this.#error(result.message);
          reported = true;
        }
        if (!result.cart) throw new Error('cart_state_unknown');
        await this.#render(result.cart, result.sections ?? {}, signal);
      } catch {
        if (signal.aborted) return;
        this.#failed = true;
        for (const control of this.querySelectorAll('button,input')) control.setAttribute('disabled', '');
        if (action.discount !== undefined && !reported) this.#discountNotice({ error: true, message: this.dataset.error, code: action.discount });
        else if (!reported) this.#error();
      } finally {
        if (this.#controller.signal === signal && !signal.aborted) {
          this.#pending--;
          this.setAttribute('aria-busy', this.#pending > 0 ? 'true' : 'false');
          this.#checkout();
          if (action.discount !== undefined && !this.#pending) {
            (this.querySelector('[data-cart-discount-form] input') ?? this.querySelector('h1'))?.focus({ preventScroll: true });
          }
        }
      }
    });
  }

  #matches(element, cart) {
    if (!element || element.dataset.sectionId !== this.dataset.sectionId) return false;
    if (Number.isFinite(cart.total_price) && Number(element.dataset.totalPrice) !== cart.total_price) return false;
    const actualCodes = cartDiscountCodes(cart).filter((entry) => entry.applicable).map((entry) => entry.code.toLowerCase()).sort();
    try {
      const renderedCodes = JSON.parse(element.dataset.discountCodes ?? '[]').map((code) => code.toLowerCase()).sort();
      if (JSON.stringify(actualCodes) !== JSON.stringify(renderedCodes)) return false;
    } catch { return false; }
    const lines = Array.from(element.querySelectorAll('[data-line-key]'));
    return lines.length === cart.items.length && cart.items.every((item) => {
      const line = lines.find((entry) => entry.dataset.lineKey === item.key);
      return Number(line?.querySelector('input')?.getAttribute('value')) === item.quantity;
    }) && (cart.item_count === 0 ? Boolean(element.querySelector('.pushe-empty')) : Boolean(element.querySelector('.pushe-summary a')));
  }

  async #render(cart, sections, signal) {
    const html = sections[this.dataset.sectionId];
    let next = typeof html === 'string'
      ? new DOMParser().parseFromString(html, 'text/html').querySelector('pushe-cart') : null;
    if (!this.#matches(next, cart)) {
      const controller = new AbortController();
      const abort = () => controller.abort();
      signal.addEventListener('abort', abort, { once: true });
      if (signal.aborted) controller.abort();
      const timer = setTimeout(abort, 10000);
      try {
        const doc = await fetchSectionDocument(this.dataset.sectionId, `${routesRoot()}cart`, { signal: controller.signal });
        controller.signal.throwIfAborted();
        next = doc.querySelector('pushe-cart');
      } finally {
        clearTimeout(timer);
        signal.removeEventListener('abort', abort);
      }
    }
    if (signal.aborted) return;
    if (!this.#matches(next, cart)) throw new Error('cart_section_mismatch');
    const active = this.ownerDocument.activeElement;
    const focusedLine = active?.closest?.('[data-line-key]');
    const key = focusedLine?.dataset.lineKey;
    const index = focusedLine ? Array.from(focusedLine.querySelectorAll('button,input,a')).indexOf(active) : -1;
    const region = this.querySelector('[data-cart-recommendations]');
    const nextRegion = next.querySelector('[data-cart-recommendations]');
    const nextIds = productIds(cart);
    const keepRecommendations = region && nextRegion && nextIds !== null && this.#recommendationsProductIds === nextIds;
    this.#extrasCleanup?.();
    if (!keepRecommendations) this.#recommendationsController?.abort();
    // The designed recommendations region is a direct child. Move it in the same replacement,
    // retaining loaded cards (or the in-flight request's target) without an empty intermediate render.
    this.replaceChildren(...Array.from(next.childNodes, (node) => keepRecommendations && node === nextRegion ? region : node));
    this.dataset.totalPrice = next.dataset.totalPrice;
    this.dataset.discountCodes = next.dataset.discountCodes;
    this.#recommendationsProductIds = nextIds;
    this.#extras(Boolean(keepRecommendations), cart);
    this.#checkout();
    if (key && index >= 0) {
      const target = this.#line(key)?.querySelectorAll('button,input,a')[index];
      (target && !target.hasAttribute('disabled') ? target : this.querySelector('h1'))?.focus({ preventScroll: true });
    }
  }

  #extras(keepRecommendations = false, cart) {
    const tools = this.querySelector('[data-cart-discount-tools]');
    if (tools) tools.hidden = false;
    if (!keepRecommendations) this.#loadRecommendations(cart);
    const bar = this.querySelector('[data-cart-sticky]');
    const summary = this.querySelector('.pp-cart > .pushe-summary');
    if (!bar || !summary || typeof IntersectionObserver !== 'function' || typeof matchMedia !== 'function') return;
    const media = matchMedia('(max-width: 899px)');
    let visible = true;
    let focusedOutside = false;
    let activeObserver = true;
    const update = () => {
      if (!activeObserver || !this.isConnected) return;
      // A focused control keeps its own space, including when the mobile keyboard opens.
      const active = this.ownerDocument.activeElement;
      const externalFocus = active && active !== this.ownerDocument.body && active !== this.ownerDocument.documentElement && !bar.contains(active);
      const focusedBar = bar.contains(active);
      const modalOpen = Array.from(this.ownerDocument.querySelectorAll('dialog[open],[aria-modal="true"]')).some(visibleModal);
      // CD: wp/wordpress.css:2611-2618 (O42). The always-rendered Shopify form is in use
      // while it holds a code or an error, even after blur. Its checkout link stays in the summary.
      const discountInUse = tools && !tools.hidden
        && (tools.querySelector('input')?.value.trim() || tools.querySelector('[role="alert"]'));
      const show = media.matches && !visible && !focusedOutside && !externalFocus && !modalOpen && !discountInUse;
      if (!show && focusedBar) summary.querySelector('a')?.focus({ preventScroll: true });
      bar.hidden = !show;
      this.classList.toggle('has-cart-sticky', show);
      if (show && typeof bar.getBoundingClientRect === 'function') this.style.setProperty('--pp-cart-sticky-space', `${bar.getBoundingClientRect().height}px`);
    };
    const observer = new IntersectionObserver((entries) => { visible = entries.some((entry) => entry.isIntersecting); update(); });
    observer.observe(summary);
    const focusIn = (event) => { focusedOutside = event.target !== this.ownerDocument.body && !bar.contains(event.target); update(); };
    const focusOut = () => { focusedOutside = false; queueMicrotask(update); };
    const resize = typeof ResizeObserver === 'function' ? new ResizeObserver(update) : null;
    resize?.observe(bar);
    const discountObserver = typeof MutationObserver === 'function' ? new MutationObserver(update) : null;
    if (tools) discountObserver?.observe(tools, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden'] });
    tools?.addEventListener('input', update);
    this.ownerDocument.addEventListener('focusin', focusIn);
    this.ownerDocument.addEventListener('focusout', focusOut);
    media.addEventListener('change', update);
    window.visualViewport?.addEventListener('resize', update);
    this.#extrasCleanup = () => {
      activeObserver = false;
      observer.disconnect();
      resize?.disconnect();
      discountObserver?.disconnect();
      tools?.removeEventListener('input', update);
      media.removeEventListener('change', update);
      this.ownerDocument.removeEventListener('focusin', focusIn);
      this.ownerDocument.removeEventListener('focusout', focusOut);
      window.visualViewport?.removeEventListener('resize', update);
      bar.hidden = true;
      this.classList.remove('has-cart-sticky');
      this.style.removeProperty('--pp-cart-sticky-space');
    };
    update();
  }

  async #loadRecommendations(cart) {
    const region = this.querySelector('[data-cart-recommendations]');
    if (!region?.dataset.productId || !region.dataset.url) return;
    const lineKeys = Array.from(this.querySelectorAll('[data-line-key]'), (line) => line.dataset.lineKey);
    const controller = new AbortController();
    this.#recommendationsController = controller;
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      if (!cart) {
        try { cart = await readCart(routesRoot(), controller.signal); } catch { /* Unknown origin: refetch on the next cart refresh. */ }
      }
      if (controller.signal.aborted || this.#recommendationsController !== controller) return;
      // Record the request's cart, only if it also describes this region's rendered cart.
      // A later mutation read may already include another tab's changes.
      this.#recommendationsProductIds = cart && lineKeys.length === cart.items.length
        && cart.items.every((item) => lineKeys.includes(item.key)) ? productIds(cart) : null;
      if (region.querySelector('.pushe-card')) return;
      for (const intent of ['complementary', 'related']) {
        const url = `${region.dataset.url}?product_id=${encodeURIComponent(region.dataset.productId)}&intent=${intent}&limit=10`;
        let next;
        try {
          const doc = await fetchSectionDocument(this.dataset.sectionId, url, { signal: controller.signal });
          next = doc.querySelector('[data-cart-recommendations]');
        } catch {
          if (controller.signal.aborted) return;
          continue;
        }
        if (controller.signal.aborted || this.#recommendationsController !== controller) return;
        if (!next?.querySelector('.pushe-card')) continue;
        region.replaceChildren(...Array.from(next.childNodes));
        region.hidden = false;
        return;
      }
    } finally { clearTimeout(timer); }
  }
}

if (typeof customElements !== 'undefined') defineOnce('pushe-cart', PusheCart);
